<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
                name: 'DashboardHome',
              }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>我的主控台</b-breadcrumb-item>
            <b-breadcrumb-item active>
              {{ displayText("download_file.list_title", "資料匯出") }}
            </b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-overlay :show="showLoading">
      <b-card>
        <div class="row d-flex mb-4 mb-xl-2 justify-content-between col-12">
          <h4 class="col-4 col-xl-6 mb-2 mb-xl-0 font-weight-bold">
            {{ displayText("download_file.list_title", "資料匯出") }}
          </h4>
          <div class="col-2">
            <b-button
              @click="fetchDownloadFiles"
              class="float-right btn-inverse-success"
            >
              <i class="fa fa-refresh fa-lg"></i>
              重新整理({{ remainingSeconds }})
            </b-button>
          </div>
        </div>
        <div class="row mt-5">
          <div class="col-12">
            <b-overlay rounded="sm">
              <b-table
                striped
                hover
                responsive
                :items="downloadFiles"
                :fields="fields"
              >
                <template #table-busy>
                  <div class="text-center my-2">
                    <b-spinner class="align-middle"></b-spinner>
                  </div>
                </template>
                <template #cell(action)="data">
                  <div class="row" style="gap: 0.5rem">
                    <b-button
                      variant="inverse-info"
                      v-if="
                        checkPermission([consts.MY_EXPORT]) &&
                        data.item.status === 'processing'
                      "
                      ><span class="mdi mdi-autorenew"></span>&nbsp;{{
                        displayText(
                          "download_file.field_status_processing",
                          "處理中"
                        )
                      }}
                    </b-button>
                    <b-button
                      variant="inverse-info"
                      v-if="
                        checkPermission([consts.MY_EXPORT]) &&
                        data.item.status === 'completed' &&
                        checkDate(data.item.expired_at)
                      "
                      :to="{
                        name: 'MyConsoleDownloadFileGetFile',
                        params: {
                          download_file_id: data.item.id,
                        },
                      }"
                      ><span class="mdi mdi-cloud-download"></span>&nbsp;{{
                        displayText(
                          "download_file.download_file_button_text",
                          "下載檔案"
                        )
                      }}
                    </b-button>
                    <div>
                      <b-button
                        id="copy-tooltip-target"
                        v-if="data.item.show_password && data.item.password != null"
                        variant="outline-info"
                        class="btn-outline-dark"
                        v-clipboard:copy="data.item.password"
                        v-b-tooltip.click="
                          displayText(
                            'download_file.copy_success_text',
                            '已複製'
                          )
                        "
                      ><span class="mdi mdi-eye"></span>&nbsp;
                        {{ data.item.password }}
                      </b-button>
                      <b-button
                        class="btn-inverse-dark"
                        v-if="
                          checkPermission([consts.MY_EXPORT]) &&
                          data.item.status === 'completed' &&
                          !data.item.show_password &&
                          data.item.password &&
                          checkDate(data.item.expired_at)
                        "
                        v-clipboard:copy="data.item.password"
                        v-b-tooltip.click="
                          displayText(
                            'download_file.copy_success_text',
                            '已複製'
                          )
                        "
                        @click="data.item.show_password = true"
                        ><span class="mdi mdi-eye-off"></span>&nbsp;{{
                          displayText(
                            "download_file.show_download_file_password_text",
                            "顯示密碼"
                          )
                        }}
                      </b-button>
                    </div>
                  </div>
                </template>
                <template #cell(during_at)="data">
                    <div>
                        {{ formatDatetime(data.item.completed_at) }}
                    </div>
                    <div>
                        {{ formatDatetime(data.item.expired_at) }}
                    </div>
                </template>
                <template #cell(creator_info)="data">
                    <div>
                        {{ data.item.staff_name }}
                    </div>
                    <div>
                        {{ formatDatetime(data.item.created_at) }}
                    </div>
                </template>
              </b-table>
            </b-overlay>
          </div>

          <CustomPagination
            :currentPage="query.page"
            :totalRows="totalRows"
            :perPage="query.per_page"
            @page-change="handlePageChange"
            @per-page-change="handlePerPageChange"
          />
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
// import _ from 'lodash'
import { format, isBefore } from "date-fns";
import downloadFileApi from "@/apis/download-file";
import PermissionChecker from "@/utils/PermissionChecker";
import * as consts from "@/consts";
import VueClipboard from "vue-clipboard2";
import Vue from "vue";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import { paginationMixin } from "@/mixins/pagination";
import { displayText } from "@/utils/dashboardUtils";

Vue.use(VueClipboard);

export default {
  components: { CustomPagination },
  mixins: [paginationMixin],
  data() {
    return {
      totalRows: 0,
      consts,
      downloadFiles: [],
      showLoading: false,
      intervalId: null,
      countdownIntervalId: null,
      remainingSeconds: 10,
      initialized: false,
      query: {
        page: 1,
        per_page: 20,
      },
    };
  },
  computed: {
    fields() {
      let table = [
        {
          key: "name",
          label: displayText("download_file.field_name", "檔案名稱"),
        },
        {
          key: "processing_at",
          label: displayText(
            "download_file.field_processing_at",
            "開始處理時間"
          ),
          formatter: (value) => {
            return value
              ? format(new Date(value), "yyyy-MM-dd HH:mm:ss")
              : null;
          },
        },
        {
          key: "during_at",
          label: displayText(
            "download_file.during_at",
            "處理完成時間/檔案過期時間"
          ),
        },
        {
          key: "status",
          label: displayText("download_file.field_status", "狀態"),
          formatter: (value) => {
            return this.getStatus(value);
          },
        },
        {
          key: "creator_info",
          label: displayText("download_file.creator_info", "執行者/建立時間"),
        },
        {
          key: "action",
          label: "管理",
        },
      ];
      return table;
    },
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      this.query = { ...to.query };
      this.fetchDownloadFiles();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  mounted() {
    this.fetchDownloadFiles();
    this.initialized = true;
    // 每秒更新剩餘倒數時間，倒數結束後自動刷新
    this.countdownIntervalId = setInterval(() => {
      if (this.remainingSeconds > 0) {
        this.remainingSeconds -= 1;
      } else {
        if (!this.showLoading) {
          this.fetchDownloadFiles();
        }
      }
    }, 1000);
  },
  methods: {
    displayText,
    formatDatetime(value) {
      return value ? format(new Date(value), "yyyy-MM-dd HH:mm:ss") : null;
    },
    checkDate(date) {
      if (isBefore(new Date(), new Date(date))) return true;
      return false;
    },
    getStatus(value) {
      if (value === "failed")
        return displayText("download_file.field_status_failed", "失敗");
      if (value === "completed")
        return displayText("download_file.field_status_completed", "完成");
      if (value === "init")
        return displayText("download_file.field_status_init", "尚未處理");
      if (value === "processing")
        return displayText("download_file.field_status_processing", "處理中");
    },
    async fetchDownloadFiles() {
      // 立即重置倒數為 10 秒
      this.remainingSeconds = 10;
      this.showLoading = true;
      try {
        const { data } = await downloadFileApi.list({ ...this.query });
        this.totalRows = data.meta.total;
        this.downloadFiles = data.data.map((df) => {
          df.show_password = false;
          return df;
        });
      } catch (e) {
        console.log(e);
      }
      this.showLoading = false;
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
  },
  beforeDestroy() {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
    }
  },
};
</script>

<style>
.table-responsive {
  min-height: 300px;
}
</style>
